import React, { useState, useEffect, useMemo } from 'react';
import Header from './Header.jsx';
import AddItemForm from './AddItemForm.jsx';
import ItemsGrid from './ItemsGrid.jsx';
import CarouselSection from './CarouselSection.jsx';
import BasicInfoSection from './BasicInfoSection.jsx';
import { Plus, X, LogOut } from 'lucide-react';
import { 
    getMenuInfo, 
    addMenuItem, 
    updateMenuItem, 
    deleteMenuItem, 
    uploadImageToCloudinary
} from '../../Firebase/Firebase';

// Cloudinary image upload function
const uploadImage = async (file) => {
  try {
    const downloadURL = await uploadImageToCloudinary(file);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image to Cloudinary:', error);
    throw error;
  }
};

const HomePage = ({ onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    customCategory: '',
    dietaryPreference: '',
    weightOptions: [],
    singlePrice: '',
    image: null,
    images: [],
    existingImageUrls: [],
    removeCover: false
  });

  // Load items on component mount
  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = () => {
    setLoading(true);
    try {
      getMenuInfo((items) => {
        setItems(items);
      });
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [items]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle weightOptions array updates
    if (name === 'weightOptions' && Array.isArray(value)) {
      setFormData(prev => ({ ...prev, weightOptions: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file, removeCover: false }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result);
      };
      reader.readAsDataURL(file);
    } else {
      // Handle image removal
      setImagePreview('');
      setFormData(prev => ({ ...prev, image: null }));
    }
  };

  const handleClearCover = () => {
    setImagePreview('');
    setFormData(prev => ({ ...prev, image: null, removeCover: true }));
  };

  // Multiple images handling
  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    // reset input so selecting the same files again re-triggers onChange
    if (e && e.target) { try { e.target.value = null; } catch (_) {} }
    if (files.length === 0) return;
    setFormData(prev => {
      const existing = Array.isArray(prev.images) ? prev.images : [];
      const combined = [...existing, ...files];
      // optional: dedupe by name+size+lastModified
      const seen = new Set();
      const deduped = combined.filter(f => {
        const key = `${f.name}|${f.size}|${f.lastModified}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return { ...prev, images: deduped };
    });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.name.trim()) {
      console.log('Validation failed: Name is empty');
      return;
    }
    
    if (!formData.dietaryPreference) {
      console.log('Validation failed: Please select a dietary preference');
      return;
    }
    
    // Check if we have weight options or single price
    const hasWeightOptions = formData.weightOptions && formData.weightOptions.length > 0;
    const hasSinglePrice = formData.singlePrice && formData.singlePrice.trim();
    
    if (!hasWeightOptions && !hasSinglePrice) {
      console.log('Validation failed: Please add either weight options or a single price');
      return;
    }
    
    // Filter out empty weight/price pairs
    const validPairs = formData.weightOptions.filter(pair => pair.weight.trim() && pair.price.trim());
    
    // Allow empty weight/price pairs array
    const finalWeightOptions = validPairs.length > 0 ? validPairs : [];

    setLoading(true);
    try {
      let imageUrl = '';
      let imageUrls = Array.isArray(formData.existingImageUrls) ? [...formData.existingImageUrls] : [];
      
      // Primary single image upload fallback
      if (formData.image) {
        try {
          imageUrl = await uploadImage(formData.image);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          imageUrl = '';
        }
      }

      // Multiple images upload
      if (formData.images && formData.images.length > 0) {
        try {
          const uploaded = await Promise.all(
            formData.images.map(async (f) => {
              try { return await uploadImage(f); } catch { return ''; }
            })
          );
          const newUrls = uploaded.filter(Boolean);
          imageUrls = [...imageUrls, ...newUrls];
        } catch (multiErr) {
          console.error('Some images failed to upload:', multiErr);
        }
      } else if (editingId) {
        const existingItem = items.find(item => item.id === editingId);
        const existingUrls = Array.isArray(formData.existingImageUrls) ? formData.existingImageUrls : (existingItem?.imageUrls || (existingItem?.imageUrl ? [existingItem.imageUrl] : []));
        imageUrls = existingUrls;
        if (!imageUrl && existingItem?.imageUrl) imageUrl = existingItem.imageUrl;
      }

      // If cover wasn't explicitly set this save, preserve existing cover for edit, or fallback to first additional for new
      if (!imageUrl) {
        if (editingId && !formData.removeCover) {
          const existingItem = items.find(item => item.id === editingId);
          const cover = existingItem?.imageUrl || (Array.isArray(existingItem?.imageUrls) ? existingItem.imageUrls[0] : '');
          if (cover) imageUrl = cover;
        }
        if (!imageUrl && imageUrls.length > 0) {
          imageUrl = imageUrls[0];
        }
      }

      // Ensure cover image is first if present and not duplicated
      if (imageUrl) {
        imageUrls = [imageUrl, ...imageUrls.filter((u) => u !== imageUrl)];
      }
      // Dedupe while preserving order
      const seen = new Set();
      imageUrls = imageUrls.filter((u) => {
        if (!u) return false;
        if (seen.has(u)) return false;
        seen.add(u);
        return true;
      });

      const itemData = {
        name: formData.name.trim(),
        category: formData.category === "Other" ? formData.customCategory.trim() : formData.category.trim(),
        dietaryPreference: formData.dietaryPreference,
        weightOptions: finalWeightOptions,
        singlePrice: hasWeightOptions ? '' : formData.singlePrice.trim(),
        imageUrl,
        imageUrls
      };

      if (editingId) {
        await updateMenuItem(editingId, itemData);
        // The items will be updated automatically through the Firebase listener
      } else {
        await addMenuItem(itemData);
        // The items will be updated automatically through the Firebase listener
      }

      closeModal();
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    const coverUrl = item.imageUrl || (Array.isArray(item.imageUrls) ? item.imageUrls[0] : '') || '';
    const allUrls = Array.isArray(item.imageUrls) ? item.imageUrls.slice() : [];
    const existingWithoutCover = allUrls.filter((u) => u && u !== coverUrl);
    setFormData({
      name: item.name,
      category: item.category || '',
      customCategory: item.category || '',
      dietaryPreference: item.dietaryPreference || '',
      weightOptions: item.weightOptions || [],
      singlePrice: item.singlePrice || '',
      image: null,
      images: [],
      existingImageUrls: existingWithoutCover,
      removeCover: false
    });
    setImagePreview(coverUrl);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    setLoading(true);
    try {
      await deleteMenuItem(id);
      // The items will be updated automatically through the Firebase listener
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    const initialFormData = { name: '', category: '', customCategory: '', dietaryPreference: '', weightOptions: [], singlePrice: '', image: null, images: [], existingImageUrls: [], removeCover: false };
    setFormData(initialFormData);
    setImagePreview('');
    setEditingId(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({ name: '', category: '', customCategory: '', dietaryPreference: '', weightOptions: [], singlePrice: '', image: null, images: [], existingImageUrls: [], removeCover: false });
    setImagePreview('');
    setEditingId(null);
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#F8E8D2]/20 to-white">
      {/* Header */}
      <Header />
      
      {/* Main Content */}
      <main className="w-full max-w-none px-6 py-8">
        {/* Logout Button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={onLogout}
            className="bg-red-500 text-white px-4 py-2 rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Menu Items Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Menu Items</h2>
            <button
              onClick={openModal}
              className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Add Item</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
          
          <ItemsGrid
            items={sortedItems}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>

        {/* Carousel Section */}
        <CarouselSection />
        
        {/* Basic Info Section */}
        <BasicInfoSection />
      </main>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-white/30 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                {editingId ? 'Edit Item' : 'Add New Item'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <AddItemForm
                editingId={editingId}
                formData={formData}
                imagePreview={imagePreview}
                loading={loading}
                onInputChange={handleInputChange}
                onImageChange={handleImageChange}
                onImagesChange={handleImagesChange}
                onClearCover={handleClearCover}
                onSubmit={handleSubmit}
                onReset={closeModal}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage; 